import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag } from 'antd';
import moment from 'moment';
import { 
  getAuditTrailLogs, 
  searchAuditEvents, 
  getAuditEventsByType, 
  getAuditEventsByUserPaginated,
  getAuditEventsByEntity
} from '../../services/auditTrailService';
import AuditTrailFilters from './AuditTrailFilters';
import AuditDetailModal from './AuditDetailModal';
import { parseChannel, channelTagColor } from './utils';

const DEFAULT_FILTERS = {
  userId: '',
  eventType: null,
  entityType: null,
  entityId: '',
  status: null,
  startDate: null,
  endDate: null,
};

const statusColors = {
  SUCCESS: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
  FAILED: { color: '#ff4d4f', bg: '#fff2f0', border: '#ffa39e' },
  PENDING: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f' },
};

const AllAuditTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [triggerMode, setTriggerMode] = useState('default');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const fetchLogs = useCallback(async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const { userId, eventType, entityType, entityId, status, startDate, endDate } = appliedFilters;
      let res;

      const hasDates = !!startDate || !!endDate;
      const hasStatus = !!status;

      if ((triggerMode === 'type_filter' && eventType) || (triggerMode === 'search' && eventType && !userId && !hasDates)) {
        res = await getAuditEventsByType(eventType);
      } else if ((triggerMode === 'user_filter' && userId) || (triggerMode === 'search' && userId && !eventType && !hasDates)) {
        res = await getAuditEventsByUserPaginated(userId, { page: page - 1, size });
      } else if ((triggerMode === 'entity_filter' && entityType && entityId) || (triggerMode === 'search' && entityType && entityId && !userId)) {
        res = await getAuditEventsByEntity(entityType, entityId);
      } else if (triggerMode === 'search') {
        res = await searchAuditEvents({
          page: page - 1, size,
          userId, eventType, entityType, status, startTime: startDate, endTime: endDate
        });
      } else {
        res = await getAuditTrailLogs({ page: page - 1, size });
      }

      if (res?.success) {
        const data = res.data?.content || res.data || [];
        setLogs(Array.isArray(data) ? data : (data.content || []));
        setPagination({ 
          current: page, 
          pageSize: size, 
          total: res.data?.totalElements || (Array.isArray(data) ? data.length : 0) 
        });
      } else {
        setLogs([]);
        setPagination(prev => ({ ...prev, total: 0 }));
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, triggerMode]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTableChange = (pag) => fetchLogs(pag.current, pag.pageSize);

  const handleApplyFilters = (mode) => {
    setAppliedFilters({ ...filters });
    setTriggerMode(mode || 'search');
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setTriggerMode('default');
  };

  const handleRowClick = (record) => {
    setSelectedLog(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (id) => (
        <span style={{ fontWeight: 600, color: '#1a1a1a', fontFamily: 'monospace' }}>{id}</span>
      ),
    },
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (type) => (
        <Tag
          style={{
            background: '#fff3eb',
            color: '#ff6700',
            border: '1px solid #ffd8b8',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {type?.replace(/_/g, ' ') || '—'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action) => <span style={{ fontWeight: 500, color: '#333' }}>{action}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc) => <span style={{ color: '#595959' }}>{desc || '—'}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const sc = statusColors[status] || statusColors.PENDING;
        return (
          <Tag
            style={{
              color: sc.color,
              background: sc.bg,
              border: `1px solid ${sc.border}`,
              borderRadius: 20,
              fontWeight: 700,
              fontSize: 11,
              padding: '2px 12px',
            }}
          >
            {status || '—'}
          </Tag>
        );
      },
    },
    {
      title: 'Channel',
      dataIndex: 'userAgent',
      key: 'channel',
      render: (ua) => {
        const channel = parseChannel(ua);
        return (
          <Tag
            color={channelTagColor(channel)}
            style={{ fontWeight: 600, borderRadius: 6, fontSize: 11 }}
          >
            {channel}
          </Tag>
        );
      },
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 140,
      render: (ip) => (
        <span style={{ fontFamily: 'monospace', color: '#595959', fontSize: 12 }}>
          {ip || '—'}
        </span>
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500 }}>
          {ts ? moment(ts).format('MMM DD, YYYY  •  hh:mm A') : '—'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AuditTrailFilters
        filters={filters}
        onFilterChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
      <div className="audit-table-card">
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} records`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'audit-row-even' : 'audit-row-odd')}
          scroll={{ x: 'max-content' }}
          className="audit-table"
        />
      </div>
      <AuditDetailModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        record={selectedLog}
      />
    </div>
  );
};

export default AllAuditTab;
